import {
  CrmPayloadType,
  WorkflowItem,
  EVENT_TYPES,
  WORKFLOW_STEP_TYPES,
  FormFieldType
} from '@care-portals/api-interfaces';

export const WORKFLOW_ACTIONS: WorkflowItem[] = [
  // ! use sendgrid adapter
  // {
  //   status: 'idle',
  //   baseType: 'action',
  //   title: 'Send message',
  //   type: WORKFLOW_STEP_TYPES.send_message,
  //   icon: 'send',
  //   description: 'Send message description'
  // },
  {
    status: 'idle',
    baseType: 'action',
    title: 'Condition',
    type: WORKFLOW_STEP_TYPES.condition,
    icon: 'check',
    description: 'Condition to continue the workflow'
  },
  {
    status: 'idle',
    baseType: 'action',
    title: 'Wait',
    type: WORKFLOW_STEP_TYPES.wait,
    icon: 'clock-circle',
    description: 'Wait for sometime, maximum 1 year'
  },
  // Todo need to implement the componenet before displaying it
  // {
  //   status: 'idle',
  //   baseType: 'action',
  //   title: 'Choice',
  //   type: WORKFLOW_STEP_TYPES.conditional_split,
  //   icon: 'partition',
  //   description: 'Conditional Split'
  // },
  {
    status: 'idle',
    baseType: 'action',
    title: 'Adapter',
    type: WORKFLOW_STEP_TYPES.adapter,
    icon: 'subnode',
    description: 'Integration with 3rd Party Platform'
  },
  {
    status: 'idle',
    baseType: 'action',
    title: 'Crm Action',
    type: WORKFLOW_STEP_TYPES.crm_action,
    icon: 'appstore',
    description: 'Do an action in the CRM'
  }
];

/** maps the workflow to its payload type based on its trigger event */
export const WORKFLOW_EVENT_PAYLOAD_TYPE: Record<EVENT_TYPES, CrmPayloadType[]> = {
  'appointment.create': ['appointment'],
  'appointment.new': ['appointment'],
  'appointment.confirmed': ['appointment'],
  'appointment.waiting': ['appointment'],
  'appointment.in-progress': ['appointment'],
  'appointment.cancelled': ['appointment'],
  'appointment.completed': ['appointment'],
  'appointment.no-show': ['appointment'],
  'appointment.closed': ['appointment'],
  'appointment.updated': ['appointment'],
  'lab_result.created': ['lab_result'],
  'lab_result.verified': ['lab_result'],
  'lead.created': ['lead'],
  'lead.converted': ['lead'],
  'lead.lost': ['lead'],
  'customer.signup': ['customer'],
  'customer.created': ['customer'],
  'customer.updated': ['customer'],
  'customer.address_updated': ['customer'],
  'customer.qnr-assigned': ['customer'],
  'form.submitted': ['form'],
  'task.cancelled': ['task'],
  'task.completed': ['task'],
  'task.created': ['task'],
  'webhook.event': ['webhook'],
  'requisition.created': ['requisition'],
  'order.created': ['order'],
  'order.subscription_cycle': ['order'],
  'order.awaiting_payment': ['order'],
  'order.processing': ['order'],
  'order.awaiting_requirements': ['order'],
  'order.awaiting_results': ['order'],
  'order.awaiting_script': ['order'],
  'order.awaiting_requisition': ['order'],
  'order.awaiting_fulfillment': ['order'],
  'order.awaiting_shipment': ['order'],
  'order.cancelled': ['order'],
  'order.shipped': ['order'],
  'order.status_updated': ['order'],
  'order.shipping_address_updated': ['order'],
  'order.rejected': ['order'],
  'order.completed': ['order'],
  'prescription.created': ['prescription'],
  'prescription.send': ['prescription'],
  'prescription.fulfillment': ['prescription'],
  'prescription.voided': ['prescription'],
  'fax.received': ['fax'],
  'questionnaire.submitted': ['questionnaire'],
  'questionnaire.reviewed': ['questionnaire'],
  'questionnaire.started': ['questionnaire'],
  'questionnaire.created': ['questionnaire'],
  'questionnaire.rejected': ['questionnaire'],
  'cart.checkout': ['cart'],
  'cart.purchased': ['cart'],
  'message.inbound': ['message'],
  'message.outbound': ['message'],
  'subscription.created': ['subscription'],
  'subscription.active': ['subscription'],
  'subscription.paused': ['subscription'],
  'subscription.cancelled': ['subscription'],
  'subscription.ended': ['subscription'],
  'subscription.upcoming-renewal': ['subscription'],
  'subscription.renewed': ['subscription'],
  'subscription.renewal_updated': ['subscription'],
  'order.payment_adjudicated': ['order'],
  'order.payment_successful': ['order'],
  'order.payment_failed': ['order'],
  'order.lab_out_for_delivery': ['order'],
  'order.lab_with_customer': ['order'],
  'order.lab_transit_lab': ['order'],
  'order.lab_delivered_to_lab': ['order'],
  'order.lab_sample_error': ['order']
};

// Exhaustive map of every pipeline. The `Record<CrmPayloadType, true>` shape
// forces this list to stay in sync with the `CrmPayloadType` union at compile
// time, so `pipelinesExcept` can never silently drop a new pipeline.
const ALL_CRM_PIPELINES_MAP: Record<CrmPayloadType, true> = {
  appointment: true,
  task: true,
  lead: true,
  form: true,
  questionnaire: true,
  webhook: true,
  order: true,
  requisition: true,
  prescription: true,
  fax: true,
  cart: true,
  customer: true,
  message: true,
  subscription: true,
  lab_result: true,
  payment: true
};

export const ALL_CRM_PIPELINES = Object.keys(
  ALL_CRM_PIPELINES_MAP
) as CrmPayloadType[];

/**
 * Returns every pipeline except the given ones. Use this to allow a CRM action
 * everywhere except the pipeline(s) it emits events to, so it cannot re-trigger
 * the workflow that runs it (which would loop).
 */
export function pipelinesExcept(
  ...excluded: CrmPayloadType[]
): CrmPayloadType[] {
  return ALL_CRM_PIPELINES.filter(pipeline => !excluded.includes(pipeline));
}

type WorkflowFunctionPipelineConfig = {
  pipelines?: CrmPayloadType[];
};

export function isWorkflowFunctionAllowed(
  functionConfig: WorkflowFunctionPipelineConfig | undefined,
  triggerEvent: EVENT_TYPES
): boolean {
  if (!functionConfig) return true;

  // If the function does not restrict pipelines, allow all workflows.
  if (!functionConfig.pipelines) return true;

  const workflowPipelines = WORKFLOW_EVENT_PAYLOAD_TYPE[triggerEvent];

  // Unknown workflow pipelines keep the historical behavior: allow the action.
  if (!workflowPipelines) return true;

  return functionConfig.pipelines.some(pipeline =>
    workflowPipelines.includes(pipeline)
  );
}

export type WorkflowIntelOptionType =
  | 'string'
  | 'object'
  | 'array'
  | 'number'
  | 'boolean';

export type WorkflowIntelOption = Record<
  string,
  {
    hint: string;
    type: WorkflowIntelOptionType;
    children?: WorkflowIntelOption;
  }
>;

export const SUBSCRIPTION_PAYLOAD: WorkflowIntelOption = {
  _id: {
    type: 'string',
    hint: 'The care portals subscription id'
  },
  organization: {
    type: 'string',
    hint: 'The care portals organization for this subscription'
  },
  customer: {
    type: 'string',
    hint: 'The customer id associated with this subscription'
  },
  product: {
    type: 'string',
    hint: 'The product id for this subscription'
  },
  phases: {
    type: 'array',
    hint: 'Array of subscription phases defining pricing and requirements',
    children: {
      price: {
        type: 'number',
        hint: 'Price for this phase in cents'
      },
      cycles: {
        type: 'number',
        hint: 'Number of cycles for this phase (0 for unlimited)'
      },
      requirements: {
        type: 'array',
        hint: 'Array of requirements for this phase',
        children: {
          type: {
            type: 'string',
            hint: 'Requirement type (e.g., "qnr" for questionnaire)'
          },
          key: {
            type: 'string',
            hint: 'Requirement key identifier'
          }
        }
      },
      fillingCycleInterval: {
        type: 'number',
        hint: 'Interval for filling cycles'
      },
      fillingCycleUnit: {
        type: 'string',
        hint: 'Unit for filling cycle interval ("day", "week", "month")'
      },
      billableCycleInterval: {
        type: 'number',
        hint: 'Interval for billable cycles'
      },
      fulfillmentCycleInterval: {
        type: 'number',
        hint: 'Interval for fulfillment cycles'
      },
      requirementCycleInterval: {
        type: 'number',
        hint: 'Interval for requirement cycles'
      }
    }
  },
  currentCycle: {
    type: 'number',
    hint: 'Current cycle number of the subscription'
  },
  nextCycleDate: {
    type: 'string',
    hint: 'ISO date string for the next cycle date'
  },
  status: {
    type: 'string',
    hint: 'Current status of the subscription (e.g., "active", "paused", "cancelled")'
  },
  statusHistory: {
    type: 'array',
    hint: 'Array of status changes for the subscription',
    children: {
      prevStatus: {
        type: 'string',
        hint: 'The previous status of the subscription'
      },
      newStatus: {
        type: 'string',
        hint: 'The new status of the subscription'
      },
      updatedAt: {
        type: 'string',
        hint: 'ISO string for the datetime of the status update'
      },
      userId: {
        type: 'string',
        hint: 'The user Id that updated the status'
      }
    }
  },
  currency: {
    type: 'string',
    hint: 'Currency code for the subscription (e.g., "USD", "CAD")'
  },
  promotionIds: {
    type: 'array',
    hint: 'Promotion IDs carried from checkout; re-applied on renewals until each promotion’s cycle limit is reached'
  },
  meta: {
    type: 'object',
    hint: 'Additional metadata for the subscription',
    children: {
      initialTransactionId: {
        type: 'string',
        hint: 'Initial payment transaction ID'
      },
      couponCode: {
        type: 'string',
        hint: 'Applied coupon code'
      },
      promotionIds: {
        type: 'array',
        hint: 'Applied promotion IDs'
      }
    }
  },
  createdAt: {
    type: 'string',
    hint: 'Created At, ISO date string'
  },
  updatedAt: {
    type: 'string',
    hint: 'Updated At, ISO date string'
  },
  __v: {
    type: 'number',
    hint: 'Mongodb __v'
  }
};

export const PRESCRIPTION_PAYLOAD: WorkflowIntelOption = {
  _id: {
    type: 'string',
    hint: 'The care portals prescription id'
  },
  drugs: {
    type: 'array',
    hint: 'Array of drugs in this prescription',
    children: {
      din: {
        type: 'string',
        hint: 'Drug Identification Number'
      },
      mitte: {
        type: 'number',
        hint: 'Quantity to dispense'
      },
      mitteType: {
        type: 'string',
        hint: 'Type of quantity unit (e.g., "pack", "tablet", "ml")'
      },
      refills: {
        type: 'number',
        hint: 'Number of refills allowed'
      },
      notes: {
        type: 'string',
        hint: 'Notes for this drug'
      },
      drugName: {
        type: 'string',
        hint: 'Name of the drug'
      }
    }
  },
  fulfillments: {
    type: 'array',
    hint: 'Array of fulfillment records for this prescription',
    children: {
      drugs: {
        type: 'array',
        hint: 'Array of drugs fulfilled',
        children: {
          din: {
            type: 'string',
            hint: 'Drug Identification Number'
          },
          qty: {
            type: 'number',
            hint: 'Quantity fulfilled'
          },
          name: {
            type: 'string',
            hint: 'Drug name'
          },
          mitte: {
            type: 'number',
            hint: 'Quantity to dispense'
          },
          refills: {
            type: 'number',
            hint: 'Number of refills'
          },
          txNumber: {
            type: 'number',
            hint: 'Transaction number'
          }
        }
      },
      orderId: {
        type: 'number',
        hint: 'Associated order ID'
      },
      status: {
        type: 'string',
        hint: 'Fulfillment status (e.g., "new", "processing", "completed")'
      },
      notes: {
        type: 'string',
        hint: 'Notes for this fulfillment'
      },
      createdAt: {
        type: 'string',
        hint: 'Fulfillment creation date, ISO date string'
      }
    }
  },
  printCounter: {
    type: 'number',
    hint: 'Number of times this prescription has been printed'
  },
  isVerbal: {
    type: 'boolean',
    hint: 'Whether this is a verbal prescription'
  },
  customer: {
    type: 'string',
    hint: 'The customer id associated with this prescription'
  },
  notes: {
    type: 'string',
    hint: 'General notes for the prescription'
  },
  createdBy: {
    type: 'string',
    hint: 'The user id who created this prescription'
  },
  organization: {
    type: 'string',
    hint: 'The care portals organization for this prescription'
  },
  createdAt: {
    type: 'string',
    hint: 'Created At, ISO date string'
  },
  updatedAt: {
    type: 'string',
    hint: 'Updated At, ISO date string'
  },
  __v: {
    type: 'number',
    hint: 'Mongodb __v'
  }
};

export const SCHEDULE_PAYLOAD: WorkflowIntelOption = {
  _id: {
    type: 'string',
    hint: 'The care portals Id for this schedule'
  },
  slots: {
    type: 'array',
    hint: 'Time slots for this schedule',
    children: {
      startMinute: {
        type: 'number',
        hint: 'The start minute of the time slot, 0 is 12AM'
      },
      endMinute: {
        type: 'number',
        hint: 'The end minute of the time slot, 0 is 12AM'
      },
      weekDay: { type: 'number', hint: 'Zero indexed week day, 0 is Sunday' }
    }
  },
  label: {
    type: 'string',
    hint: 'The label of the schedule'
  },
  endDate: {
    type: 'string',
    hint: 'The end date of the schedule, ISO date string'
  },
  startDate: {
    type: 'string',
    hint: 'The end date of the schedule, ISO date string'
  },
  overrides: {
    type: 'array',
    hint: 'The schedule overrides',
    children: {
      id: {
        type: 'string',
        hint: 'Unique id for the override when the override is coming from syncing a calendar'
      },
      date: {
        type: 'string',
        hint: 'The date of the override ISO date string'
      },
      startMinute: {
        type: 'number',
        hint: 'The start minute of the override, 0 is 12AM'
      },
      endMinute: {
        type: 'number',
        hint: 'The end minute of the override, 0 is 12AM'
      },
      isAvailable: {
        type: 'boolean',
        hint: 'If true the override will be an availability, else it will mark the schedule as unavailable for the duration of the override'
      }
    }
  },
  organization: {
    type: 'string',
    hint: 'The care portals organization for this schedule'
  },
  createdAt: { type: 'string', hint: 'Created At, ISO date string' },
  updatedAt: { type: 'string', hint: 'Created At, ISO date string' },
  __v: { type: 'number', hint: 'Mongodb __v' },
  extras: {
    type: 'object',
    hint: 'Custom data object for the schedule'
  },
  provider: {
    type: 'string',
    hint: 'The care portals EMR provider ID for this schedule'
  }
};

export const FORM_PAYLOAD: WorkflowIntelOption = {
  _id: {
    type: 'string',
    hint: 'The care portals form id'
  },
  fields: {
    type: 'array',
    hint: 'The fields of this form',
    children: {
      label: { type: 'string', hint: 'The field label' },
      name: { type: 'string', hint: 'The field name' },
      type: { type: 'string', hint: 'The field type' },
      defaultValue: { type: 'string', hint: 'The field default value' },
      required: { type: 'boolean', hint: 'Is the field required' },
      mandatory: {
        type: 'boolean',
        hint: " can't be removed in form builder and has to exist, applied to email field"
      },
      isHidden: {
        type: 'boolean',
        hint: 'The field is hidden from UI and will get its value programmatically'
      },
      variant: {
        type: 'string',
        hint: 'The variant of the field'
      },
      placeholder: {
        type: 'string',
        hint: 'The placeholder of the field'
      },
      width: {
        type: 'string',
        hint: 'The width of the field'
      }
    }
  },
  name: { type: 'string', hint: 'The form name' },
  title: { type: 'string', hint: 'The form label' },
  description: {
    type: 'string',
    hint: 'The form description, only for admin use, will not show to the customer'
  },
  organization: {
    type: 'string',
    hint: 'The care portals organization for this form'
  },
  createdAt: { type: 'string', hint: 'Created At, ISO date string' },
  updatedAt: { type: 'string', hint: 'Created At, ISO date string' },
  __v: { type: 'number', hint: 'Mongodb __v' }
};

export const SERVICE_PAYLOAD: WorkflowIntelOption = {
  _id: {
    type: 'string',
    hint: 'The care portals service id'
  },
  isQueue: {
    type: 'boolean',
    hint: 'Is the service queue type'
  },
  extraTimePerDependent: {
    type: 'number',
    hint: 'Extra time added to the service duration for each dependent'
  },
  extraPricePerDependent: {
    type: 'number',
    hint: 'Extra cost added to the service price for each dependent'
  },
  isVirtualMeeting: {
    type: 'boolean',
    hint: 'Is the service a virtual meeting'
  },
  fields: {
    type: 'array',
    hint: '*** DEPRECATED *** use `service.form` instead'
  },
  schedules: {
    type: 'array',
    hint: 'All the schedules assigned to the service',
    children: SCHEDULE_PAYLOAD
  },
  isHidden: {
    type: 'boolean',
    hint: 'A flag to mark the visibility of this service in the booking app'
  },
  maxLeadTime: {
    type: 'number',
    hint: 'The amount of hours a customer can book in the future'
  },
  minLeadTime: {
    type: 'number',
    hint: 'The amount of hours from now that a customer cant book in'
  },
  slotGap: {
    type: 'number',
    hint: 'The amount of minutes to be unavailable between booked appointments'
  },
  slotDuration: {
    type: 'number',
    hint: 'The duration of the service in minutes'
  },
  order: {
    type: 'number',
    hint: 'The sort order of the service in the booking app, default is 999'
  },
  label: { type: 'string', hint: 'The label of the service' },
  status: { type: 'string', hint: 'The status of the service' },
  description: { type: 'string', hint: 'The description of the service' },
  instructions: {
    type: 'string',
    hint: 'The instructions of the service, will appear for the user in the booking app'
  },
  organization: {
    type: 'string',
    hint: 'The care portals organization for this service'
  },
  createdAt: { type: 'string', hint: 'Created At, ISO date string' },
  updatedAt: { type: 'string', hint: 'Created At, ISO date string' },
  __v: { type: 'number', hint: 'Mongodb __v' },
  extras: {
    type: 'object',
    hint: 'Custom data object for the service'
  },
  form: {
    type: 'object',
    hint: 'The form assigned for this service',
    children: FORM_PAYLOAD
  },
  location: {
    type: 'string',
    hint: 'The location of this service'
  },
  payment: {
    type: 'object',
    hint: 'The payment data for this service',
    children: {
      enabled: {
        type: 'boolean',
        hint: 'Is the payment enable for this service (mark as paid service)'
      },
      payLater: {
        type: 'boolean',
        hint: 'Authorize payment method only and do not make a charge'
      },
      hcCoverage: {
        type: 'number',
        hint: 'The amount of health card coverage for this service, min is 0 (no coverage), max is 100 (full coverage)'
      },
      price: { type: 'number', hint: 'The price of the service in cents' },
      currency: {
        type: 'string',
        hint: 'The currency of the price, USD or CAD'
      },
      qtyField: { type: 'string', hint: 'The quantity field name' }
    }
  },
  duration: { type: 'number', hint: 'The duration of the service in minutes' },
  assignmentStrategy: {
    type: 'string',
    hint: 'The assignment strategy of the service, can be `round-robin`, `queue`, or `none`'
  }
};

export const LEAD_PAYLOAD: WorkflowIntelOption = {
  _id: {
    type: 'string',
    hint: 'The care portals lead id'
  },
  uname: {
    type: 'string',
    hint: 'The unique name for this lead in this organization'
  },
  __v: { type: 'number', hint: 'Mongodb __v' },
  createdAt: { type: 'string', hint: 'Created At, ISO date string' },
  dependents: {
    type: 'array',
    hint: 'Dependents'
    // TODO children
  },
  email: {
    type: 'string',
    hint: 'The lead email'
  },
  extras: {
    type: 'object',
    hint: 'Custom data object for the service'
  },
  firstName: {
    type: 'string',
    hint: 'The lead first name'
  },
  lastName: {
    type: 'string',
    hint: 'The lead last name'
  },
  hc: {
    type: 'object',
    hint: 'The lead health card data',
    children: {
      hcExp: { type: 'string', hint: 'Health card exp' },
      hcNum: { type: 'string', hint: 'Health card number' },
      hcVer: { type: 'string', hint: 'Health card version' },
      hcType: { type: 'string', hint: 'Health card type' },
      number: { type: 'string', hint: 'Health card number' },
      version: { type: 'string', hint: 'Health card version' },
      effectiveDate: { type: 'string', hint: 'Health card eff' },
      expiryDate: { type: 'string', hint: 'Health card exp' },
      type: { type: 'string', hint: 'Health card type' }
    }
  },
  isPatient: {
    type: 'boolean',
    hint: 'Has this lead signed up to the patient portal'
  },
  lastActivityAt: {
    type: 'string',
    hint: 'ISO string of the last activity of this lead'
  },
  meta: {
    type: 'object',
    hint: 'Object contains metadata of the lead'
  },
  organization: {
    type: 'string',
    hint: 'The care portals organization for this lead'
  },
  rating: { type: 'number', hint: 'The lead rating' },
  status: { type: 'string', hint: 'The lead status' },
  updatedAt: { type: 'string', hint: 'Created At, ISO date string' },

  phone: { type: 'string', hint: 'The lead phone number' },
  address: {
    type: 'object',
    hint: 'The lead address data',
    children: {
      address_1: { type: 'string', hint: 'The lead address 1' },
      address_2: { type: 'string', hint: 'The lead address 2' },
      city: { type: 'string', hint: 'The lead city' },
      postal: { type: 'string', hint: 'The lead postal code' },
      province: { type: 'string', hint: 'The lead province' },
      country: { type: 'string', hint: 'The lead country' }
    }
  }
};

export const APPOINTMENT_PAYLOAD: WorkflowIntelOption = {
  _id: {
    type: 'string',
    hint: 'The care portals appointment Id'
  },
  organization: {
    type: 'string',
    hint: 'The care portals organization for this appointment'
  },
  accessKey: {
    type: 'string',
    hint: 'The access key for the appointment for public api use'
  },
  status: {
    type: 'string',
    hint: 'The status of the appointment'
  },
  service: {
    type: 'object',
    hint: 'The selected service for this appointment',
    children: SERVICE_PAYLOAD
  },
  schedule: {
    type: 'object',
    hint: 'The schedule assigned for this appointment',
    children: SCHEDULE_PAYLOAD
  },
  reservation: {
    type: 'string',
    hint: 'ISO string of the reservation datetime of the appointment'
  },
  isQueue: { type: 'boolean', hint: 'Is a queue type appointment' },
  relatedToLead: {
    type: 'object',
    hint: 'The lead assigned for this appointment',
    children: LEAD_PAYLOAD
  },
  createdBy: {
    type: 'string',
    hint: 'The id of the user that created the appointment, if it is created by a provider or admin and not by the customer'
  },
  duration: {
    type: 'number',
    hint: 'The duration of the appointment in minutes, this will override the service duration'
  },
  meta: {
    type: 'object',
    hint: 'Object contains metadata of the appointment'
  },
  urgency: { type: 'number', hint: 'The urgency of the appointment' },
  statusHistory: {
    type: 'array',
    hint: 'The status history if the appointment',
    children: {
      prevStatus: {
        type: 'string',
        hint: 'The previous status of the appointment'
      },
      newStatus: { type: 'string', hint: 'The new status of the appointment' },
      updatedAt: {
        type: 'string',
        hint: 'ISO string for the datetime of the status update'
      },
      userId: {
        type: 'string',
        hint: 'The user Id that updated the status, if it is updated by customer the value will be `customer`'
      }
    }
  },
  createdAt: { type: 'string', hint: 'Created At, ISO date string' },
  updatedAt: { type: 'string', hint: 'Updated At, ISO date string' },
  __v: { type: 'number', hint: 'Mongodb __v' },
  reservationDate: {
    type: 'string',
    hint: 'The reservation date of the appointment YYYY-MM-DD'
  },
  reservationTime: {
    type: 'string',
    hint: 'The reservation time of the appointment H:mma, ex. `9:00AM`'
  },
  reservationOrgTz: {
    type: 'string',
    hint: 'The reservation time zone, ex. `(GMT-5) America/Toronto`'
  },
  reservationLocalDate: {
    type: 'string',
    hint: 'The reservation local date of the appointment YYYY-MM-DD'
  },
  reservationLocalTime: {
    type: 'string',
    hint: 'The reservation local time of the appointment H:mma, ex. `9:00AM`'
  },
  reservationLocalTz: {
    type: 'string',
    hint: 'The reservation local time zone, ex. `(GMT-5) America/Toronto`'
  },
  minutesTillReservation: {
    type: 'number',
    hint: 'The number of minutes remaining until the appointment starts'
  }
};

export const CUSTOMER_PAYLOAD: WorkflowIntelOption = {
  _id: {
    type: 'string',
    hint: 'The care portals customer id'
  },
  organization: {
    type: 'string',
    hint: 'The care portals organization for this customer'
  },
  __v: { type: 'number', hint: 'Mongodb __v' },
  uname: { type: 'string', hint: 'The customer username (email#organization)' },
  email: { type: 'string', hint: 'The customer email' },
  phone: { type: 'string', hint: 'The customer phone number' },
  firstName: { type: 'string', hint: 'The customer first name' },
  lastName: { type: 'string', hint: 'The customer last name' },
  dob: {
    type: 'string',
    hint: 'Date of birth (ISO date string, ex: 1990-01-01)'
  },
  referrer: { type: 'string', hint: 'Referrer information' },
  source: {
    type: 'string',
    hint: 'Source of customer registration [signup, manual]'
  },
  notificationsCount: {
    type: 'number',
    hint: 'Number of notifications for the customer'
  },
  isInsuranceAvailable: {
    type: 'boolean',
    hint: 'Whether insurance is available for the customer'
  },
  acceptMarketing: {
    type: 'boolean',
    hint: 'Whether the customer accepts marketing communications'
  },
  extras: {
    type: 'object',
    hint: 'Additional customer information and preferences'
  },
  addresses: { type: 'array', hint: 'Array of customer addresses' },
  defaultAddress: {
    type: 'object',
    hint: 'The customer default address',
    children: {
      address1: { type: 'string', hint: 'The customer address line 1' },
      address2: { type: 'string', hint: 'The customer address line 2' },
      city: { type: 'string', hint: 'The customer city' },
      postalCode: { type: 'string', hint: 'The customer postal code' },
      provinceCode: { type: 'string', hint: 'The customer province code' },
      countryCode: { type: 'string', hint: 'The customer country code' }
    }
  },
  identifiers: { type: 'array', hint: 'Array of customer identifiers' },
  watchlist: { type: 'array', hint: 'Customer providers watchlist' },
  usedCoupons: { type: 'array', hint: 'Array of coupons used by the customer' },
  assignedQuestionnaires: {
    type: 'array',
    hint: 'Array of questionnaires assigned to the customer'
  },
  providers: {
    type: 'array',
    hint: 'Array of healthcare providers associated with the customer'
  },
  groups: {
    type: 'array',
    hint: 'Array of groups the customer belongs to'
  },
  createdAt: { type: 'string', hint: 'Created At, ISO date string' },
  updatedAt: { type: 'string', hint: 'Updated At, ISO date string' }
};

export const ORDER_PAYLOAD: WorkflowIntelOption = {
  _id: { type: 'string', hint: 'Order unique id' },
  organization: {
    type: 'string',
    hint: 'The care portals organization for this order'
  },
  id: { type: 'number', hint: 'Order numeric id' },
  customer: {
    type: 'object',
    hint: 'Customer information for the order',
    children: {
      _id: { type: 'string', hint: 'Customer MongoDB id' },
      email: { type: 'string', hint: 'Customer email' },
      phone: { type: 'string', hint: 'Customer phone number' },
      firstName: { type: 'string', hint: 'Customer first name' },
      lastName: { type: 'string', hint: 'Customer last name' },
      bigcommerceId: { type: 'string', hint: 'Customer BigCommerce id' },
      stripeId: { type: 'string', hint: 'Customer Stripe id' }
    }
  },
  status: {
    type: 'string',
    hint: 'Order status (e.g., pending, completed, cancelled)'
  },
  statusHistory: {
    type: 'array',
    hint: 'Array of order status changes',
    children: {
      prevStatus: {
        type: 'string',
        hint: 'The previous status of the order'
      },
      newStatus: { type: 'string', hint: 'The new status of the order' },
      updatedAt: {
        type: 'string',
        hint: 'ISO string for the datetime of the status update'
      },
      userId: {
        type: 'string',
        hint: 'The user Id that updated the status'
      }
    }
  },
  source: { type: 'string', hint: 'Order source [checkout, manual]' },
  lineItems: {
    type: 'array',
    hint: 'Array of line items in the order',
    children: {
      id: { type: 'string', hint: 'Line item unique id' },
      parent_id: {
        type: 'string',
        hint: 'Parent line item id (null for top-level items)'
      },
      variant_id: { type: 'number', hint: 'Product variant id' },
      product_id: { type: 'number', hint: 'Product id' },
      sku: { type: 'string', hint: 'SKU' },
      name: { type: 'string', hint: 'Product name' },
      url: { type: 'string', hint: 'Product URL' },
      quantity: { type: 'number', hint: 'Quantity ordered' },
      taxable: { type: 'boolean', hint: 'Whether the item is taxable' },
      image_url: { type: 'string', hint: 'Product image URL' },
      discounts: {
        type: 'array',
        hint: 'Array of discounts applied to the item'
      },
      coupons: { type: 'array', hint: 'Array of coupons applied to the item' },
      discount_amount: { type: 'number', hint: 'Total discount amount' },
      coupon_amount: { type: 'number', hint: 'Total coupon amount' },
      original_price: { type: 'number', hint: 'Original price per unit' },
      list_price: { type: 'number', hint: 'List price per unit' },
      sale_price: { type: 'number', hint: 'Sale price per unit' },
      extended_list_price: {
        type: 'number',
        hint: 'Total list price (price × quantity)'
      },
      extended_sale_price: {
        type: 'number',
        hint: 'Total sale price (price × quantity)'
      },
      is_require_shipping: {
        type: 'boolean',
        hint: 'Whether the item requires shipping'
      },
      is_mutable: { type: 'boolean', hint: 'Whether the item can be modified' },
      is_custom_price: {
        type: 'boolean',
        hint: 'Whether the item has a custom price'
      },
      added_by_promotion: {
        type: 'boolean',
        hint: 'Whether the item was added by a promotion'
      },
      options: {
        type: 'array',
        hint: 'Array of product options/configurations'
      },
      custom_fields: {
        type: 'object',
        hint: 'Custom fields for the product'
      },
      productId: {
        type: 'number',
        hint: 'Product id (duplicate of product_id)'
      },
      variantId: {
        type: 'number',
        hint: 'Variant id (duplicate of variant_id)'
      },
      price: { type: 'number', hint: 'Final price per unit' }
    }
  },
  shippingAddress: {
    type: 'object',
    hint: 'Shipping address for the order',
    children: {
      company: { type: 'string', hint: 'Company name' },
      address1: { type: 'string', hint: 'Address line 1' },
      address2: { type: 'string', hint: 'Address line 2' },
      city: { type: 'string', hint: 'City' },
      province: { type: 'string', hint: 'Province/State name' },
      provinceCode: { type: 'string', hint: 'Province/State code' },
      country: { type: 'string', hint: 'Country name' },
      countryCode: { type: 'string', hint: 'Country code' },
      postalCode: { type: 'string', hint: 'Postal/ZIP code' }
    }
  },
  pharmacyAddress: {
    type: 'object',
    hint: 'Pharmacy address for the order',
    children: {
      name: { type: 'string', hint: 'Pharmacy name' },
      phoneNumber: { type: 'string', hint: 'Pharmacy phone number' },
      faxNumber: { type: 'string', hint: 'Pharmacy fax number' },
      address1: { type: 'string', hint: 'Address line 1' },
      address2: { type: 'string', hint: 'Address line 2' },
      city: { type: 'string', hint: 'City' },
      provinceCode: { type: 'string', hint: 'Province/State code' },
      countryCode: { type: 'string', hint: 'Country code (CA or US)' },
      postalCode: { type: 'string', hint: 'Postal/ZIP code' }
    }
  },
  invoices: {
    type: 'array',
    hint: 'Array of invoices associated with the order'
  },
  totalAmount: {
    type: 'number',
    hint: 'Total order amount including taxes and fees'
  },
  discountAmount: { type: 'number', hint: 'Total discount amount applied' },
  baseAmount: { type: 'number', hint: 'Base order amount before discounts' },
  assignedTo: {
    type: 'object',
    hint: 'User assigned to the order',
    children: {
      _id: { type: 'string', hint: 'Assigned user id' },
      firstName: { type: 'string', hint: 'Assigned user first name' },
      lastName: { type: 'string', hint: 'Assigned user last name' }
    }
  },
  assignedPharmacy: {
    type: 'object',
    hint: 'Pharmacy assigned to fulfill the order',
    children: {
      _id: { type: 'string', hint: 'Assigned pharmacy id' },
      name: { type: 'string', hint: 'Assigned pharmacy name' }
    }
  },
  assignedPhysicianNetwork: {
    type: 'object',
    hint: 'Physician network assigned to the order',
    children: {
      name: { type: 'string', hint: 'Physician network display name' },
      key: { type: 'string', hint: 'Physician network key (beluga, mdi, arora)' }
    }
  },
  physicianNetworkVisit: {
    type: 'object',
    hint: 'Network-side visit / encounter snapshot',
    children: {
      source: { type: 'string', hint: 'Physician network key' },
      visitId: { type: 'string', hint: 'Network-side visit id' },
      patientId: { type: 'string', hint: 'Network-side patient id' },
      orderId: { type: 'string', hint: 'Network-side order id' },
      status: { type: 'string', hint: 'Network-side status (observational)' }
    }
  },
  assignedToGroup: {
    type: 'string',
    hint: 'Group/organization assigned to fulfill the order'
  },
  requirements: {
    type: 'array',
    hint: 'Array of special requirements for the order'
  },
  isDeleted: {
    type: 'boolean',
    hint: 'Whether the order is marked as deleted'
  },
  transferOut: {
    type: 'boolean',
    hint: 'Whether the order is marked for transfer out'
  },
  creditUsedAmount: {
    type: 'number',
    hint: 'Amount of store credit used for the order'
  },
  postPurchaseCredit: {
    type: 'number',
    hint: 'Store credit awarded after purchase'
  },
  createdAt: {
    type: 'string',
    hint: 'Order creation timestamp (ISO date string)'
  },
  updatedAt: {
    type: 'string',
    hint: 'Order last update timestamp (ISO date string)'
  },
  __v: { type: 'number', hint: 'MongoDB version key' },
  shipments: {
    type: 'array',
    hint: 'Array of shipment details for the order',
    children: {
      carrier: {
        type: 'string',
        hint: 'Shipping carrier name (e.g. fedex, ups)'
      },
      service: {
        type: 'string',
        hint: 'Specific shipping service used'
      },
      cost: {
        type: 'number',
        hint: 'Shipping cost'
      },
      trackingCode: {
        type: 'string',
        hint: 'Carrier tracking number'
      },
      trackingLink: {
        type: 'string',
        hint: 'URL to track shipment'
      },
      items: {
        type: 'array',
        hint: 'Items included in this shipment',
        children: {
          name: {
            type: 'array',
            hint: 'Product name(s)'
          },
          quantity: {
            type: 'number',
            hint: 'Quantity shipped'
          },
          sku: {
            type: 'array',
            hint: 'Product SKU(s)'
          }
        }
      }
    }
  },
  shippedAt: {
    type: 'string',
    hint: 'Timestamp when order was shipped (ISO date string)'
  },
  trackingCode: {
    type: 'string',
    hint: 'Primary tracking number for the order'
  },
  trackingLink: {
    type: 'string',
    hint: 'URL to track the primary shipment'
  },
  lab: {
    type: 'object',
    hint: 'At-home lab kit status and shipment details',
    children: {
      status: {
        type: 'string',
        hint:
          'Lab kit lifecycle (e.g. requisition_created, out_for_delivery, with_customer, transit_lab, delivered_to_lab, sample_error, completed)'
      },
      shipmentToPatient: { type: 'object', hint: 'Tracking for kit sent to patient' },
      shipmentToLab: { type: 'object', hint: 'Tracking for kit returned to lab' }
    }
  }
};

export const LAB_RESULT_PAYLOAD: WorkflowIntelOption = {
  _id: { type: 'string', hint: 'Lab result document id' },
  organization: { type: 'string', hint: 'Organization slug' },
  customer: {
    type: 'object',
    hint: 'Patient the lab result belongs to',
    children: {
      _id: { type: 'string', hint: 'Customer id' },
      firstName: { type: 'string', hint: 'First name' },
      lastName: { type: 'string', hint: 'Last name' },
      email: { type: 'string', hint: 'Email' },
      dob: { type: 'string', hint: 'Date of birth' }
    }
  },
  labName: { type: 'string', hint: 'Laboratory name' },
  collectedAt: { type: 'string', hint: 'Collection date (ISO)' },
  results: {
    type: 'array',
    hint: 'Individual test rows',
    children: {
      testName: { type: 'string', hint: 'Test name' },
      value: { type: 'number', hint: 'Numeric value' },
      units: { type: 'string', hint: 'Units' },
      status: { type: 'string', hint: 'normal | abnormal' },
      refRange: { type: 'string', hint: 'Reference range text' }
    }
  },
  docUrl: { type: 'string', hint: 'Linked document URL if any' },
  source: { type: 'string', hint: 'fax | patient_upload | manual' },
  createdAt: { type: 'string', hint: 'Created at (ISO)' },
  updatedAt: { type: 'string', hint: 'Updated at (ISO)' }
};

export const REQUISITION_PAYLOAD: WorkflowIntelOption = {
  _id: { type: 'string', hint: 'Lab requisition id' },
  brand: { type: 'string', hint: 'Organization / brand' },
  customer: {
    type: 'object',
    hint: 'Patient',
    children: {
      _id: { type: 'string', hint: 'Customer id' },
      firstName: { type: 'string', hint: 'First name' },
      lastName: { type: 'string', hint: 'Last name' },
      email: { type: 'string', hint: 'Email' },
      phone: { type: 'string', hint: 'Phone' }
    }
  },
  templateId: { type: 'string', hint: 'Requisition template id' },
  templateVariant: { type: 'string', hint: 'Template variant' },
  data: { type: 'object', hint: 'Structured requisition field values' },
  dueDate: { type: 'string', hint: 'Due date (ISO)' },
  pdfKey: { type: 'string', hint: 'S3 key for PDF if stored' },
  createdAt: { type: 'string', hint: 'Created at (ISO)' },
  updatedAt: { type: 'string', hint: 'Updated at (ISO)' }
};

export const Form_Field_Type_INTELLISENSE_TYPE_MAP: Record<
  FormFieldType,
  WorkflowIntelOptionType
> = {
  text: 'string',
  area: 'string',
  email: 'string',
  number: 'number',
  phone: 'object',
  phone164: 'string',
  date: 'string',
  file: 'object',
  section: 'string',
  polar: 'boolean',
  switch: 'boolean',
  consent: 'boolean',
  singleselect: 'string',
  multiselect: 'array',
  ontario_hc: 'object',
  creditcard: 'object',
  group: 'array'
};

export const FORM_FIELD_TYPE_INTELLISENSE_CHILDREN_MAP: Record<
  FormFieldType,
  WorkflowIntelOption
> = {
  text: null,
  area: null,
  email: null,
  number: null,
  phone: {
    code: {
      type: 'string',
      hint: 'Country code number'
    },
    num: {
      type: 'string',
      hint: 'Phone number'
    }
  },
  phone164: null,
  date: null,
  file: {
    key: {
      type: 'string',
      hint: 'The file key is s3'
    },
    name: {
      type: 'string',
      hint: 'The name of the form field'
    },
    originalName: {
      type: 'string',
      hint: 'The original name of the file'
    },
    mimetype: {
      type: 'string',
      hint: 'The mimetype of the file'
    },
    encoding: {
      type: 'string',
      hint: 'The file encoding'
    }
  },
  section: null,
  polar: null,
  switch: null,
  consent: null,
  singleselect: null,
  multiselect: null,
  ontario_hc: {
    hcNum: {
      type: 'string',
      hint: 'Health card number'
    },
    hcVer: {
      type: 'string',
      hint: 'Health card version'
    },
    hcExp: {
      type: 'string',
      hint: 'Health card expiry date'
    },
    hcEff: {
      type: 'string',
      hint: 'Health card effective date'
    }
  },
  creditcard: {
    id: {
      type: 'string',
      hint: 'Stripe token ID'
    },
    object: {
      type: 'string',
      hint: 'Value equal to "token"'
    },
    type: {
      type: 'string',
      hint: 'Stripe Token Type, value is "card"'
    },
    client_ip: {
      type: 'string',
      hint: 'Client IP address'
    },
    created: {
      type: 'number',
      hint: 'Unix date'
    },
    livemode: {
      type: 'boolean',
      hint: 'Is production, false is for testing'
    },
    used: {
      type: 'boolean',
      hint: 'Is used'
    },
    card: {
      type: 'object',
      hint: 'Stripe Token Type',
      children: {
        id: {
          type: 'string',
          hint: 'Stripe card ID'
        },
        object: {
          type: 'string',
          hint: 'Value equal to "card"'
        },
        brand: {
          type: 'string',
          hint: 'The brand of the credit card'
        },
        country: {
          type: 'string',
          hint: 'The country of the credit card'
        },
        exp_month: {
          type: 'number',
          hint: 'Expiry month of the card'
        },
        exp_year: {
          type: 'number',
          hint: 'Expiry year of the card'
        },
        fingerprint: {
          type: 'string',
          hint: 'Fingerprint of the card'
        },
        funding: {
          type: 'string',
          hint: 'Value is "credit"'
        },
        last4: {
          type: 'string',
          hint: 'Last 4 digits of the card'
        },
        metadata: {
          type: 'object',
          hint: 'Card metadata'
        }
      }
    }
  },
  // TODO groups
  group: null
};
